import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import Skeleton from 'react-loading-skeleton';

/**
 * Contract-period picker — design option 4A, "Grouped picker".
 *
 * Replaces the horizontal period tab row. That row put every contract a company has ever had on one
 * line, so it overflowed sideways: the current period sat at the far left, the oldest scrolled off the
 * right, and picking last year meant hunting along a strip of near-identical date ranges. As a control
 * it also read as page navigation rather than as a filter on the page you were already looking at.
 *
 * This states the selected period once and opens a grouped list on demand. Current and previous sit
 * apart from the archive, so the two periods anyone actually asks for are never buried in the list.
 *
 * Accepts either tab-row API, so every call site is a rename rather than a rewrite:
 *
 *   common/molecules/Tabbar    list of objects + currentPage + setContract
 *   tailwind/molecules/Tabbar  list of strings + ids/newPageId + changeHandler
 */

// Each state needs to be legible at 10px, so the tints are the DS 100-level fills with their matching
// 500-level text rather than a near-white gray. `gray-100` (#F9FAFB) reads as no badge at all.
const TAG_STYLES = {
  Current: 'bg-ds-green-lt text-ds-green-dk',
  Previous: 'bg-ds-blue-lt text-ds-blue-dk',
  Overall: 'bg-ds-brand-lt text-ds-brand-dk',
  default: 'bg-gray-200 text-gray-700',
};

/**
 * Current / Previous / Overall / Archive. Prefers the flags ContractsManager sets, and falls back to the
 * label — one of the two tab rows this replaces passes plain strings.
 */
const tagFor = ({ text = '', source }) => {
  if (source && source.is_overall_contract) return 'Overall';
  if (text === 'Overall') return 'Overall';
  if (source && source.is_running_contract) return 'Current';
  if (text.startsWith('Current')) return 'Current';
  if (text.startsWith('Previous')) return 'Previous';
  return 'Archive';
};

/**
 * The design writes periods as "Feb 2026 – Feb 2027": four-digit years, en dash, and no Current/Previous
 * prefix — the tag beside the label already says that, so repeating it would say the same thing twice.
 *
 * `ContractsManager` instead produces "Current (Feb ‘26 - Feb ‘27)", and its `text` is the selection key
 * that call sites compare against, so it is reformatted for display here rather than changed at source.
 * Dates are reformatted from `start_date`/`end_date` where they are available, and parsed out of the
 * label where the call site only passes a string.
 */
const formatRange = (item) => {
  const { source, text = '' } = item;

  // "Overall" is a named aggregate, not a range. It carries start/end dates, so without this it would
  // render as a span of years through the object API and as the word "Overall" through the string one —
  // the same row reading two different ways depending on the call site.
  if (tagFor(item) === 'Overall') return text;

  if (source && typeof source === 'object' && source.start_date && source.end_date) {
    return `${moment(source.start_date).format('MMM YYYY')} – ${moment(source.end_date).format('MMM YYYY')}`;
  }

  const inner = /^(?:Current|Previous)\s*\((.+)\)$/.exec(text);
  return (inner ? inner[1] : text)
    .replace(/[‘’']\s*(\d{2})\b/g, (match, year) => `20${year}`)
    .replace(/\s*-\s*/g, ' – ');
};

const PeriodPicker = ({
  list,
  currentPage,
  setContract,
  ids,
  newPageId,
  changeHandler,
  classes = '',
  loading = false,
  label = 'Contract/Policy period',
  disabled = false,
  // Shown instead of the selected period's range. OPD Overview needs this: across several companies at
  // once there is no single contract to name, so it says "Current contract" and locks the control.
  valueLabel,
}) => {
  // Unique per instance: the visible label is wired to the button with aria-labelledby, and more than one
  // picker can appear on a page, so a hardcoded id would make them ambiguous. Option ids are namespaced
  // to the same instance for the same reason.
  const instanceId = useId();
  const labelId = `period-label-${instanceId}`;
  const valueId = `period-value-${instanceId}`;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  // Set when the keyboard moved the active row, cleared by the pointer. Only a keyboard move may scroll:
  // hovering a partially visible row would otherwise scroll it flush, sliding a different row under a
  // stationary cursor, which fires mouseenter again and can ping-pong.
  const keyboardNav = useRef(false);

  // Normalised to { text, id, source } so the rest of the component is indifferent to which API the call
  // site used. `source` is the original entry, which is what `setContract` expects back.
  const items = useMemo(() => {
    const raw = Array.isArray(list) ? list : [];
    return raw
      .map((entry, index) => {
        if (entry && typeof entry === 'object') {
          return { text: entry.text, id: entry.contract_id != null ? entry.contract_id : entry.text, source: entry };
        }
        return {
          text: entry,
          id: Array.isArray(ids) && ids.length > index ? ids[index] : entry,
          source: entry,
        };
      })
      .filter((entry) => entry.text);
  }, [list, ids]);

  // The tab row this replaces was uncontrolled — it tracked the selection in its own state, which quietly
  // covered for call sites passing a constant `currentPage`. This component is controlled, so it must not
  // do the same: clamping a miss to index 0 would show "Current" while the page displayed another period's
  // data. A miss is only defaulted to the newest period when the caller named no selection at all;
  // naming one that does not match is a wiring bug and renders a placeholder so it is visible.
  const hasExplicitSelection = (newPageId != null && newPageId !== '')
    || (currentPage != null && currentPage !== '');
  const matched = newPageId != null && newPageId !== ''
    ? items.findIndex((item) => `${item.id}` === `${newPageId}`)
    : items.findIndex((item) => item.text === currentPage);
  const selectedIndex = matched >= 0 ? matched : (hasExplicitSelection ? -1 : 0);
  const selected = items[selectedIndex];

  // "Active" is whatever is not an archived year: the running period, the one before it, and the
  // Overall aggregate some benefit types append. Everything older is archive.
  //
  // `list`/`ids` are built inline at every call site, so these memos get fresh deps each render and never
  // actually hit. They are kept because the work is trivially cheap either way and they pay off the day a
  // caller stabilises its list — not because they are doing anything today.
  const groups = useMemo(() => {
    const active = [];
    const archive = [];
    items.forEach((item, index) => {
      const entry = { item, index, tag: tagFor(item) };
      (entry.tag === 'Archive' ? archive : active).push(entry);
    });

    // ContractsManager only writes "Current" while today falls inside the range, and "Previous" only for
    // a contract that ended within the last year. A company whose renewal has lapsed gets neither, so
    // every row would be tagged Archive and the newest period — the one selected by default — would be
    // buried under an "Archive" heading. Promote the newest entry so the group a reader looks in first is
    // never empty. Items arrive newest-first from ContractsManager.
    if (active.length === 0 && archive.length > 0) active.push(archive.shift());

    return [
      { key: 'active', heading: 'Active', entries: active },
      { key: 'archive', heading: 'Archive', entries: archive },
    ].filter((group) => group.entries.length > 0);
  }, [items]);

  const flat = useMemo(() => groups.flatMap((group) => group.entries), [groups]);

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const choose = useCallback(
    (item) => {
      // `changeHandler` is the tailwind Tabbar's contract and receives the id as a string, because it
      // originally came from `event.currentTarget.id`.
      if (changeHandler) changeHandler(`${item.id}`);
      else if (setContract) setContract(item.source);
      close();
      // Focus returns to the trigger so a keyboard user is not dropped at the top of the document.
      if (triggerRef.current) triggerRef.current.focus();
    },
    [setContract, changeHandler, close]
  );

  // Close on an outside click or Escape — a dropdown that can only be dismissed by picking something
  // traps the reader into changing the period just to get rid of it.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) close();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        close();
        if (triggerRef.current) triggerRef.current.focus();
      }
    };

    // Deliberately not closed on scroll: the list stays open and scrolls with the page. It cannot ride
    // over the fixed navbar because its z-index sits below it — see the dropdown's `z-[9]` below.
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  const openList = () => {
    if (disabled) return;
    setActiveIndex(flat.findIndex((entry) => entry.index === selectedIndex));
    setOpen(true);
  };

  const onTriggerKeyDown = (event) => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      if (open) return;
      openList();
    }
  };

  const onListKeyDown = (event) => {
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) keyboardNav.current = true;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((current) => {
        const next = current + step;
        if (next < 0) return flat.length - 1;
        if (next >= flat.length) return 0;
        return next;
      });
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      setActiveIndex(event.key === 'Home' ? 0 : flat.length - 1);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const entry = flat[activeIndex];
      if (entry) choose(entry.item);
    }
  };

  if (loading && items.length === 0) {
    return (
      <div className={classes}>
        <Skeleton width={248} height={58} />
      </div>
    );
  }

  if (items.length === 0) return null;

  // No badge at all when nothing is selected — defaulting to "Archive" would state something untrue.
  const selectedTag = selected ? tagFor(selected) : null;

  return (
    <div className={classes}>
      <div ref={containerRef} className='relative inline-flex flex-col gap-[7px]'>
        <span id={labelId} className='text-ts-xs font-semibold uppercase tracking-[0.08em] text-gray-600'>{label}</span>

        <button
          ref={triggerRef}
          type='button'
          disabled={disabled}
          onClick={() => (open ? close() : openList())}
          onKeyDown={onTriggerKeyDown}
          aria-haspopup='listbox'
          // Both ids: `aria-labelledby` *replaces* the button's contents as its accessible name, so
          // pointing it at the label alone announced only the label and never the selected period.
          aria-labelledby={`${labelId} ${valueId}`}
          aria-expanded={open}
          className={`flex min-w-[248px] items-center justify-between gap-[10px] rounded-ds-md border border-gray-300 bg-white px-ds-3 py-ds-2 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-brand focus-visible:ring-offset-1 ${
            disabled ? 'cursor-not-allowed opacity-60' : 'hover:border-gray-400'
          }`}
        >
          <span
            id={valueId}
            className={`font-jetbrains-mono text-[13px] font-semibold tabular-nums ${
              selected || valueLabel ? 'text-gray-900' : 'text-gray-500'
            }`}
          >
            {valueLabel || (selected ? formatRange(selected) : 'Select period')}
          </span>
          {/* Suppressed when the value is overridden: across several companies the trigger reads
              "Current contract" while `selected` may still be an archive period, which would put a
              CURRENT CONTRACT label next to an ARCHIVE badge. */}
          {!valueLabel && selectedTag ? (
            <span
              className={`rounded-ds-pill px-ds-2 py-[4px] text-ts-2xs font-semibold uppercase tracking-[0.08em] ${
                TAG_STYLES[selectedTag] || TAG_STYLES.default
              }`}
            >
              {selectedTag}
            </span>
          ) : null}
          {/* The design draws this as a 10px `▾` text glyph, which is too small to read at a glance and
              renders differently on every platform. Same position and colour, drawn as a 14px stroked
              chevron so it is legible and consistent. */}
          <svg
            aria-hidden
            viewBox='0 0 24 24'
            className={`h-3.5 w-3.5 shrink-0 text-gray-600 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
            fill='none'
            stroke='currentColor'
            strokeWidth='2.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='m6 9 6 6 6-6' />
          </svg>
        </button>

        {open ? (
          <div
            role='listbox'
            tabIndex={-1}
            onKeyDown={onListKeyDown}
            aria-activedescendant={flat[activeIndex] ? `period-option-${instanceId}-${flat[activeIndex].index}` : undefined}
            ref={(node) => {
              // Focus only when the list first mounts, not on every re-render.
              const isNew = node && listRef.current !== node;
              listRef.current = node;
              if (isNew) node.focus();
            }}
            // `w-full` so the list is exactly as wide as the control it drops from, and `mt-[6px]` so it
            // sits clear of it — the design overlapped the two by 6px, which read as a rendering fault.
            // `no-scrollbar` (src/index.css) keeps the overflow scrollable but hides the bar, which would
            // otherwise eat into the 6px padding and sit against the rounded corner.
            // Right-aligned: every call site places the control at the right of a header row, so the list
            // hangs from that edge and cannot overflow the viewport. z-[9] deliberately sits below the
            // fixed navbar (z-10) and sidebar (z-20) so app chrome is never covered, while still painting
            // above the page's own unlayered content.
            className='no-scrollbar absolute right-0 top-full z-[9] mt-[6px] max-h-[300px] w-full overflow-y-auto rounded-ds-md border border-gray-300 bg-white p-[6px] shadow-ds-lg focus:outline-none'
          >
            {/* `role='group'` rather than a bare wrapper: an unnamed div between the listbox and its
                options makes the option tree look broken to some assistive tech, and naming the group
                from its own heading is what tells you "Archive" applies to the rows beneath it. */}
            {groups.map((group, groupIndex) => (
              <div key={group.key} role='group' aria-labelledby={`period-group-${instanceId}-${group.key}`}>
                <span
                  id={`period-group-${instanceId}-${group.key}`}
                  className={`block px-[10px] pb-1 text-ts-2xs font-semibold uppercase tracking-[0.09em] text-gray-600 ${
                    groupIndex === 0 ? 'pt-ds-2' : 'mt-[6px] border-t border-gray-300 pt-ds-3'
                  }`}
                >
                  {group.heading}
                </span>
                {group.entries.map((entry) => {
                  const isSelected = entry.index === selectedIndex;
                  const isActive = flat[activeIndex] && flat[activeIndex].index === entry.index;

                  return (
                    <div
                      key={`${entry.item.id}-${entry.item.text}`}
                      id={`period-option-${instanceId}-${entry.index}`}
                      role='option'
                      aria-selected={isSelected}
                      // The list caps at 300px (~7 rows), so arrowing past the seventh entry would move
                      // `aria-activedescendant` onto a row nobody can see. `block: 'nearest'` scrolls only
                      // when the row is actually outside the viewport, so it never jumps on hover.
                      ref={(node) => {
                        if (isActive && node && keyboardNav.current) node.scrollIntoView({ block: 'nearest' });
                      }}
                      onClick={() => choose(entry.item)}
                      onMouseEnter={() => {
                        keyboardNav.current = false;
                        setActiveIndex(flat.findIndex((row) => row.index === entry.index));
                      }}
                      className={`flex cursor-pointer items-center justify-between gap-[10px] rounded-ds-sm px-[10px] py-ds-2 hover:bg-gray-100 ${
                        isSelected ? 'bg-ds-brand/10' : isActive ? 'bg-gray-100' : ''
                      }`}
                    >
                      <span
                        className={`font-jetbrains-mono text-[12px] tabular-nums ${
                          isSelected ? 'font-semibold text-ds-brand' : 'font-medium text-gray-800'
                        }`}
                      >
                        {formatRange(entry.item)}
                      </span>
                      {/* Every row states its own status, so the archive years are not the only ones you
                          can identify at a glance. The selected row is already carrying the brand fill and
                          brand text, which is what marks it — a tick alongside that said it twice. */}
                      <span
                        className={`shrink-0 rounded-ds-pill px-ds-2 py-[4px] text-ts-2xs font-semibold uppercase tracking-[0.08em] ${
                          TAG_STYLES[entry.tag] || TAG_STYLES.default
                        }`}
                      >
                        {entry.tag}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PeriodPicker;
